import { CHTooltip } from '@/components/tooltip';
import { DS_COLOR } from '@calm/evexia-design-tokens';
import { useAudio } from '@rhg/hooks/use-audio';
import playerMessages from '@rhg/lang/messages/player';
import {
    Box,
    IconButton,
    IconButtonProps,
    Slide,
    Typography,
    useMediaQuery,
    useTheme,
} from '@rhg/libs/material';
import {
    Back15,
    CHStop,
    ClosedCaptionFilled,
    ClosedCaptionOutline,
    Forward15,
    InfoFilled,
    PauseFilled,
    PlayFilled,
    SkipBack,
    SkipForward,
} from '@rhg/libs/material/icons';
import { styleMaxLines, sxAppend } from '@rhg/libs/material/sx';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { MediaSubtitles } from '../../subtitles';
import { AudioTimeline } from '../timeline';
import {
    AudioControlsProps,
    AudioPlayerControls,
    AudioPlayerVariant,
    AudioTimelineVariant,
} from '../types';

const styleSubtitles = {
    pb: 16,
    pt: 10,
};

const styleFloatingSubtitles = {
    background: `linear-gradient(180deg, #00000000 0%, ${DS_COLOR.BLACK_20P} 100%)`,
};

const styleCenteredSubtitlesWrapper = {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

const styleIconButton = {
    mx: 4,
    width: { xs: 32, sm: 32, md: 48 },
    height: { xs: 32, sm: 32, md: 48 },
    '& svg': {
        width: 32,
        height: 32,
    },
};

const styleIconButtonLeft = { ml: 0 };
const styleIconButtonRight = { mr: 0 };

const styleIconButtonSmall = {
    mx: 9,
    width: 24,
    height: 24,
    '& svg': {
        width: 24,
        height: 24,
    },
};

const styleAudioControlsContainer = {
    width: '100%',
};

const styleFloatingContainer = {
    backgroundColor: 'background.paper',
};

const styleControlsMain = {
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: {
        xs: 'center',
        sm: 'center',
        md: 'center',
        lg: 'space-between',
    },
    alignItems: 'center',
    position: 'relative',
    minHeight: '48px',
};

const styleNonMobileElement = {
    display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' },
    flexBasis: { lg: '42%', xl: '28%' },
};

const styleMetadataContainer = {
    flexBasis: { lg: '28%', xl: '36%' },
};

const styleCustomActionContainer = {
    justifyContent: 'flex-end',
    flexBasis: { lg: '28%', xl: '36%' },
};

const styleControlsCenter = {
    width: '100%',
    position: { xs: 'static', sm: 'static', md: 'static', lg: 'absolute' },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
};

const styleControlsButtons = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
};

const styleControlsButtonsTimeline = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'stretch',
    flexGrow: 0,
};

const styleControlsButtonsNoTimeline = {
    mb: 4,
};

const styleTitleSubtitle = { ml: 4 };

const styleTitleContainer = {
    display: 'flex',
    alignItems: 'baseline',
    gap: 2,
};

const styleDrawerTitleIcon = {
    '& svg': {
        height: '16px',
        width: '16px',
    },
};
const styleMetadata = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
};

export function AudioControls({
    areControlsHidden,
    sx,
    controls = AudioPlayerControls.Regular,
    variant = AudioPlayerVariant.Collapsed,
    isFloating = false,
    title,
    subtitle,
    image,
    customAction,
    onStop,
    controlsSx,
    onNextTrack,
    onPreviousTrack,
    onControlClick,
    drawerTitleIcon,
}: AudioControlsProps) {
    const { formatMessage } = useIntl();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const [isTooltipOpen, setIsTooltipOpen] = useState(true);

    const isSoundScape = useMemo(
        () => controls === AudioPlayerControls.Soundscape,
        [controls]
    );

    const isPlaylist = useMemo(
        () => controls === AudioPlayerControls.Playlist,
        [controls]
    );

    const {
        controls: {
            isMuted,
            isShowingSubtitles,
            hasSubtitles,
            isPaused,
            currentCue,
            timingState,
            togglePlay,
            toggleSubtitles,
            setTime,
        },
        hasAutoPlayRestriction,
    } = useAudio();

    const iconButtonStyle = useMemo(
        () =>
            variant === AudioPlayerVariant.Collapsed
                ? styleIconButtonSmall
                : styleIconButton,
        [variant]
    );

    const ccIcon = useMemo(() => {
        return isShowingSubtitles && hasSubtitles ? (
            <ClosedCaptionFilled color="info" />
        ) : (
            <ClosedCaptionOutline color="inherit" />
        );
    }, [hasSubtitles, isShowingSubtitles]);

    const onRewind = useCallback(
        () => setTime(Math.max(timingState.currentTime - 15, 0)),
        [setTime, timingState.currentTime]
    );

    const onForward = useCallback(
        () =>
            setTime(
                Math.min(timingState.currentTime + 15, timingState.duration)
            ),
        [setTime, timingState.currentTime, timingState.duration]
    );

    const previousActionProps = useMemo<IconButtonProps>(() => {
        return {
            trackButton: isPlaylist
                ? 'AudioPlayerPreviousTrack'
                : 'AudioPlayerBack15',
            'aria-label': isPlaylist
                ? formatMessage(playerMessages['player.previous_track'])
                : formatMessage(playerMessages['player.backward_15']),
            onClick: isPlaylist ? onPreviousTrack : onRewind,
            sx: iconButtonStyle,
        };
    }, [iconButtonStyle, isPlaylist, onPreviousTrack, onRewind, formatMessage]);

    const previousActionIcon = useMemo(() => {
        return isPlaylist ? <SkipBack /> : <Back15 />;
    }, [isPlaylist]);

    const nextActionProps = useMemo<IconButtonProps>(() => {
        return {
            trackButton: isPlaylist
                ? 'AudioPlayerNextTrack'
                : 'AudioPlayerForward15',
            'aria-label': isPlaylist
                ? formatMessage(playerMessages['player.next_track'])
                : formatMessage(playerMessages['player.forward_15']),
            onClick: isPlaylist ? onNextTrack : onForward,
            sx: iconButtonStyle,
        };
    }, [iconButtonStyle, isPlaylist, onForward, onNextTrack, formatMessage]);

    const nextActionIcon = useMemo(() => {
        return isPlaylist ? <SkipForward /> : <Forward15 />;
    }, [isPlaylist]);

    const isDisplayingSubtitles =
        variant !== AudioPlayerVariant.Collapsed &&
        isShowingSubtitles &&
        !isSoundScape;

    const handleTogglePlay = () => {
        if (isPaused && hasAutoPlayRestriction) {
            setIsTooltipOpen(false);
        }
        if (onControlClick) {
            // The reverse of the current state, prior to calling togglePlay
            onControlClick(isPaused ? 'play' : 'pause');
        }

        togglePlay();
    };

    return (
        <>
            {isDisplayingSubtitles && (
                <Box sx={styleCenteredSubtitlesWrapper}>
                    <MediaSubtitles
                        fadeIn={areControlsHidden}
                        cue={currentCue}
                        isMuted={isMuted}
                        sx={isFloating ? styleFloatingSubtitles : undefined}
                    />
                </Box>
            )}
            <Slide
                direction="up"
                in={!areControlsHidden}
                mountOnEnter
                unmountOnExit
            >
                <Box sx={sx}>
                    {isDisplayingSubtitles && (
                        <MediaSubtitles
                            cue={currentCue}
                            isMuted={isMuted}
                            sx={sxAppend(
                                styleSubtitles,
                                isFloating && styleFloatingSubtitles
                            )}
                        />
                    )}
                    <Box
                        sx={sxAppend(
                            styleAudioControlsContainer,
                            isFloating && styleFloatingContainer,
                            controlsSx
                        )}
                    >
                        <Box sx={styleControlsMain}>
                            <Box
                                sx={sxAppend(
                                    styleNonMobileElement,
                                    styleMetadataContainer
                                )}
                            >
                                <Box sx={styleMetadata}>
                                    {image}
                                    {!isMobile && (title || subtitle) && (
                                        <Box sx={styleTitleSubtitle}>
                                            <Box sx={styleTitleContainer}>
                                                {drawerTitleIcon ? (
                                                    <Box
                                                        sx={
                                                            styleDrawerTitleIcon
                                                        }
                                                    >
                                                        {drawerTitleIcon}
                                                    </Box>
                                                ) : null}
                                                <Typography
                                                    variant="h6"
                                                    component="p"
                                                    color="text.body"
                                                    sx={styleMaxLines(2)}
                                                >
                                                    {title}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="subtitle2"
                                                component="p"
                                                color="text.sub"
                                                sx={styleMaxLines(1)}
                                            >
                                                {subtitle}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            <Box sx={styleNonMobileElement}></Box>
                            <Box sx={styleControlsCenter}>
                                <Box
                                    sx={sxAppend(
                                        styleControlsButtonsTimeline,
                                        variant ===
                                            AudioPlayerVariant.Expanded &&
                                            styleControlsButtonsNoTimeline
                                    )}
                                >
                                    <Box sx={styleControlsButtons}>
                                        {variant !==
                                            AudioPlayerVariant.Collapsed && (
                                            <IconButton
                                                disabled={
                                                    !hasSubtitles ||
                                                    isSoundScape
                                                }
                                                trackButton={
                                                    isShowingSubtitles
                                                        ? 'AudioPlayerDisableCC'
                                                        : 'AudioPlayerEnableCC'
                                                }
                                                onClick={() =>
                                                    toggleSubtitles()
                                                }
                                                aria-label={
                                                    isShowingSubtitles
                                                        ? formatMessage(
                                                              playerMessages[
                                                                  'player.disable_cc'
                                                              ]
                                                          )
                                                        : formatMessage(
                                                              playerMessages[
                                                                  'player.enable_cc'
                                                              ]
                                                          )
                                                }
                                                sx={sxAppend(
                                                    iconButtonStyle,
                                                    styleIconButtonLeft
                                                )}
                                            >
                                                {ccIcon}
                                            </IconButton>
                                        )}
                                        {!isSoundScape && (
                                            <IconButton
                                                {...previousActionProps}
                                                sx={sxAppend(
                                                    previousActionProps.sx ??
                                                        {},
                                                    variant ===
                                                        AudioPlayerVariant.Collapsed &&
                                                        styleIconButtonLeft
                                                )}
                                            >
                                                {previousActionIcon}
                                            </IconButton>
                                        )}
                                        {hasAutoPlayRestriction ? (
                                            <CHTooltip
                                                isOpen={isTooltipOpen}
                                                startIcon={<InfoFilled />}
                                                topLabel={formatMessage(
                                                    playerMessages[
                                                        'player.autoplay_error.top_label'
                                                    ]
                                                )}
                                                content={formatMessage(
                                                    playerMessages[
                                                        'player.autoplay_error.content'
                                                    ]
                                                )}
                                            >
                                                <IconButton
                                                    trackButton={
                                                        timingState.isPaused
                                                            ? 'AudioPlayerPlay'
                                                            : 'AudioPlayerPause'
                                                    }
                                                    onClick={handleTogglePlay}
                                                    aria-label={
                                                        timingState.isPaused
                                                            ? formatMessage(
                                                                  playerMessages[
                                                                      'player.play'
                                                                  ]
                                                              )
                                                            : formatMessage(
                                                                  playerMessages[
                                                                      'player.pause'
                                                                  ]
                                                              )
                                                    }
                                                    sx={iconButtonStyle}
                                                >
                                                    {timingState.isPaused ? (
                                                        <PlayFilled />
                                                    ) : (
                                                        <PauseFilled />
                                                    )}
                                                </IconButton>
                                            </CHTooltip>
                                        ) : (
                                            <IconButton
                                                trackButton={
                                                    timingState.isPaused
                                                        ? 'AudioPlayerPlay'
                                                        : 'AudioPlayerPause'
                                                }
                                                onClick={handleTogglePlay}
                                                aria-label={
                                                    timingState.isPaused
                                                        ? formatMessage(
                                                              playerMessages[
                                                                  'player.play'
                                                              ]
                                                          )
                                                        : formatMessage(
                                                              playerMessages[
                                                                  'player.pause'
                                                              ]
                                                          )
                                                }
                                                sx={iconButtonStyle}
                                            >
                                                {timingState.isPaused ? (
                                                    <PlayFilled />
                                                ) : (
                                                    <PauseFilled />
                                                )}
                                            </IconButton>
                                        )}
                                        {!isSoundScape && (
                                            <IconButton
                                                {...nextActionProps}
                                                sx={sxAppend(
                                                    iconButtonStyle,
                                                    variant !==
                                                        AudioPlayerVariant.Expanded &&
                                                        styleIconButtonRight
                                                )}
                                            >
                                                {nextActionIcon}
                                            </IconButton>
                                        )}
                                        {variant ===
                                            AudioPlayerVariant.Expanded && (
                                            <IconButton
                                                trackButton="AudioPlayerStop"
                                                onClick={onStop}
                                                sx={sxAppend(
                                                    iconButtonStyle,
                                                    styleIconButtonRight
                                                )}
                                                aria-label={formatMessage(
                                                    playerMessages[
                                                        'player.stop'
                                                    ]
                                                )}
                                            >
                                                <CHStop />
                                            </IconButton>
                                        )}
                                    </Box>
                                    {variant === AudioPlayerVariant.Collapsed &&
                                        !isSoundScape && (
                                            <AudioTimeline
                                                variant={
                                                    AudioTimelineVariant.Small
                                                }
                                            />
                                        )}
                                </Box>
                            </Box>
                            <Box
                                sx={sxAppend(
                                    styleNonMobileElement,
                                    styleCustomActionContainer
                                )}
                            >
                                {customAction}
                            </Box>
                        </Box>
                    </Box>
                    {variant === AudioPlayerVariant.Expanded &&
                        !isSoundScape && <AudioTimeline />}
                </Box>
            </Slide>
        </>
    );
}
